import {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import PageShell from '../components/PageShell';
import {decideReservation, getDoctorQueue} from '../api/ReservationApi';
import {saveComplaint} from '../api/ComplaintApi';

const formatTime = (iso) => new Date(iso).toLocaleString();

/** The signed-in dentist's consultation queue: approve/reject requests, write up visits. */
export default function DoctorReservations() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getDoctorQueue(status)
      .then(setRows)
      .catch((err) => setError(err.message));
  }, [status]);

  useEffect(load, [load]);

  const decide = async (id, decision) => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await decideReservation(id, decision, notes[id] || '');
      setMessage(decision === 'approve' ? 'Consultation approved.' : 'Request declined.');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const writeUp = async (id) => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await saveComplaint(id, {doctor_summary: notes[id] || ''});
      setMessage('Summary saved.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageShell title="My consultations (dentist)" showBackButton={true}>
      <div className="container py-3" style={{maxWidth: 720}}>
        <div className="btn-group mb-3" role="group" aria-label="Filter">
          {['pending', 'confirmed'].map((s) => (
            <button
              key={s}
              type="button"
              className={`btn ${status === s ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => {
                // Drop the other tab's rows right away so their buttons never act on the wrong tab.
                setRows([]);
                setMessage(null);
                setError(null);
                setStatus(s);
              }}
              aria-pressed={status === s}
            >
              {s === 'pending' ? 'Awaiting approval' : 'Upcoming'}
            </button>
          ))}
        </div>
        {message && <p role="status">{message}</p>}
        {error && (
          <p role="alert" className="text-danger">
            {error}
          </p>
        )}
        {rows.length === 0 && <p>Nothing here.</p>}
        <ul className="list-unstyled">
          {rows.map((r) => (
            <li key={r.id} className="card mb-2">
              <div className="card-body">
                <h2 className="h6">{formatTime(r.reservation_time)}</h2>
                <label htmlFor={`notes-${r.id}`} className="form-label">
                  {status === 'pending' ? 'Note to patient' : 'Visit summary'}
                </label>
                <textarea
                  id={`notes-${r.id}`}
                  className="form-control mb-2"
                  rows={2}
                  value={notes[r.id] || ''}
                  onChange={(e) => setNotes((n) => ({...n, [r.id]: e.target.value}))}
                />
                {status === 'pending' ? (
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => decide(r.id, 'approve')}
                      disabled={busy}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => decide(r.id, 'reject')}
                      disabled={busy}
                    >
                      Decline
                    </button>
                  </div>
                ) : (
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => navigate(`/virtual-consultation/room/${r.id}`)}
                    >
                      Join video
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() => writeUp(r.id)}
                      disabled={busy}
                    >
                      Save summary
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </PageShell>
  );
}
